import { Helmet } from 'react-helmet-async'
import { Link } from "../navigation";
import { Trans, useTranslation } from 'react-i18next'
import _ from 'lodash'

import { PlainImage } from '../components/Image'
import { TrademarkFamily, TrademarkProductMapping, useTrademarks } from './TrademarksProvider'
import { trademarkFamilyUrl } from './utils'
import { useFilteredTrademarks } from '../filter/FilteredTrademarks'
import { EmptyFilteredPortfolio, EmptyPortfolio } from './TrademarkPortfolio'
import { commodityUrl } from '../products/products'
import { useFilteredCommodities } from '../filter/FilteredCommodities'
import { useLocalState } from 'src/settings/localStorage';
import ToggleButton from 'src/components/ToggleButton';
import { trademark_family } from 'src/data';
import { ProductMappingMatrix } from '../products/ProductMappingMatrix'

export default function TrademarkProductMappings() {
    const { t } = useTranslation()

    const { trademarkFamilies: _families } = useFilteredTrademarks()
    const { trademarkProductMappings, deleteTrademarkProductMapping, postTrademarkProductMapping, trademarkFamilies: allFamilies } = useTrademarks()
    const { commodities } = useFilteredCommodities()
    const [showPictures, setShowPictures] = useLocalState('tm-pm-show-pictures', true)

    const families = _.sortBy(_families, 'reference')

    if (families.length === 0 || commodities.length === 0) {
        return <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('product-mapping')} | {t('trademarks')} | Patent Cockpit</title>
            </Helmet>
            <div className="header-row">
                <div className="flex flex-row items-center max-w-2xl gap-2">
                    <h2 className="modern-h2 grow">{t('product-mapping')}</h2>
                </div>
            </div>
            <div className="main-content grow">
                {commodities.length === 0
                    ? <Trans
                        i18nKey="add-products-hint"
                        components={{ portfoliolink: <Link className="text-pcx-500 underline-link" to="/products/portfolio" /> }}
                    />
                    : allFamilies?.length > 0
                        ? <EmptyFilteredPortfolio />
                        : <EmptyPortfolio />}
            </div>
        </>
    }

    return <ProductMappingMatrix<TrademarkFamily, TrademarkProductMapping>
        pageTitle={`${t('product-mapping')} | ${t('trademarks')} | Patent Cockpit`}
        heading={t('product-mapping')}
        families={families}
        commodities={commodities}
        links={trademarkProductMappings}
        getFamilyId={f => f.familyId}
        renderFamilyHeader={family => <>
            <Link className="underline-link whitespace-nowrap" to={trademarkFamilyUrl(family)}>{family.reference}</Link>
            <p className="text-xs truncate">{family.name}</p>
            {showPictures &&
                <div className="max-w-[8rem] h-12 shrink-0">
                    <PlainImage
                        entity={trademark_family}
                        entityId={family.familyId}
                        title={family.reference}
                        text={family.description}
                        clickable={false}
                    />
                </div>}
        </>}
        renderCommodityCell={commodity => <>
            <Link
                className="underline-link block pl-3 bg-white dark:bg-pcx-100"
                to={commodityUrl(commodity)}
            >{commodity.commodityReference}</Link>
            {showPictures &&
                <div className="w-28 h-12">
                    <PlainImage
                        entity="commodity"
                        entityId={commodity.commodityId}
                        clickable={false}
                    />
                </div>}
        </>}
        baseLinkFor={(commodityId, family) => ({ commodityId, familyId: family.familyId })}
        isEqualLink={(a, b) => a.commodityId === b.commodityId && a.familyId === b.familyId}
        postLinks={postTrademarkProductMapping}
        deleteLinks={deleteTrademarkProductMapping}
        headerExtras={
            <label className="flex flex-row items-center gap-2">
                <span>{t('show-pictures')}</span>
                <ToggleButton checked={showPictures} setChecked={setShowPictures} />
            </label>
        }
    />
}
